import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { UpcomingReservationDashboardDto } from 'api/dashboard/dtos/UpcomingReservationDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetPeriodSelect, { PeriodOption } from '../components/WidgetPeriodSelect';
import KpiHeader from '../components/KpiHeader';
import useInfiniteReveal from '../components/useInfiniteReveal';
import upcomingStyles from '../upcomingEvents/UpcomingEvents.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './UpcomingReservations.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const PERIOD_OPTIONS: PeriodOption[] = [
	{ value: '2', labelKey: 'dashboard.period_2_days' },
	{ value: '7', labelKey: 'dashboard.period_7_days' },
	{ value: '30', labelKey: 'dashboard.period_1_month' },
	{ value: '90', labelKey: 'dashboard.period_3_months' },
	{ value: 'custom', labelKey: 'dashboard.period_custom' },
];

type Props = {
	data?: UpcomingReservationDashboardDto[] | null;
	isLoading?: boolean;
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const formatPeriod = (start: string, end: string, t: (k: string) => string) => {
	const s = dayjs(start);
	const e = dayjs(end);
	const today = dayjs().startOf('day');
	const tomorrow = today.add(1, 'day');

	let prefix = s.format('DD/MM');
	if (s.isSame(today, 'day')) prefix = t('dashboard.upcoming_reservations.today');
	else if (s.isSame(tomorrow, 'day')) prefix = t('dashboard.upcoming_reservations.tomorrow');

	return `${prefix}  ${s.format('HH:mm')} – ${e.format('HH:mm')}`;
};

const UpcomingReservations = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<UpcomingReservationDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [period, setPeriod] = useState<string>('2');
	const { dateFrom: customFrom, dateTo: customTo, setDateRange } = useWidgetFilters('upcomingReservations');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const isLegacyMode = props.data !== undefined;

	const getDateRange = () => {
		if (period === 'custom') {
			return { dateFrom: customFrom, dateTo: customTo };
		}
		const days = parseInt(period, 10);
		return {
			dateFrom: dayjs().startOf('day').toDate(),
			dateTo: dayjs().add(days, 'day').endOf('day').toDate(),
		};
	};

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const range = getDateRange();
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom: range.dateFrom, dateTo: range.dateTo, ...vehicleScope };
				const result = await DashboardService.getUpcomingReservations(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, period, customFrom, customTo]);

	const data = isLegacyMode ? props.data ?? [] : fetchedData ?? [];
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const buildVehicleTooltip = (item: UpcomingReservationDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.upcoming_reservations.title') as string}
			subTitleTooltip={t('dashboard.upcoming_reservations.tooltip' as any) as string}
			renderOptions={!isLegacyMode ? (
				<WidgetPeriodSelect
					value={period}
					options={PERIOD_OPTIONS}
					onChange={setPeriod}
					dateFrom={customFrom}
					dateTo={customTo}
					onDateRangeChange={setDateRange}
				/>
			) : undefined}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(data.length)}
						subLabel={t('dashboard.upcoming_reservations.total_upcoming' as any) as string}
						currentTotal={data.length}
						previousTotal={null}
						previousRange={null}
					/>
					<div className={upcomingStyles.tableWrapper}>
						<table className={upcomingStyles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.upcoming_reservations.col_plate')}</th>
									<th>{t('dashboard.upcoming_reservations.col_driver')}</th>
									<th>{t('dashboard.upcoming_reservations.col_period')}</th>
									<th>{t('dashboard.upcoming_reservations.col_reason')}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map(item => (
									<tr
										key={item.reservationId}
										className={upcomingStyles.tableRow}
										onClick={() => window.open('/reservations', '_blank')}
									>
										<td>
											<span
												className={styles.plate}
												data-tip={buildVehicleTooltip(item)}
												data-for="upcoming-reservations-tooltip"
												data-place="right"
											>
												{item.registrationNumber}
											</span>
										</td>
										<td>{item.driverName || <span className={styles.muted}>—</span>}</td>
										<td>{formatPeriod(item.startDate, item.endDate, k => t(k as any) as string)}</td>
										<td>{item.reason || <span className={styles.muted}>—</span>}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={4} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="upcoming-reservations-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default UpcomingReservations;
